"use client";

import Image from 'next/image';
import Link from 'next/link';
import vilvaLogoMark from "@/public/images/assets/vilva-logo.webp";
import gopiSir from "@/public/images/assets/gopi-sir.webp";
import React, { useState, useEffect, useRef } from 'react';
import ReactSelect from "react-select";
import dynamic from 'next/dynamic';
const Select = dynamic(() => import('react-select'), { ssr: false }) as typeof ReactSelect;
import type ReCAPTCHABase from 'react-google-recaptcha';

const ReCAPTCHA = dynamic(() => import('react-google-recaptcha'), {
  ssr: false,
  loading: () => (
    <div className="h-[78px] w-[304px] bg-gray-100 rounded animate-pulse" />
  ),
}) as typeof ReCAPTCHABase;

import toast from "react-hot-toast";

import { getFlagUrl } from "@/lib/imageUrl";
import { getApiUrl } from "@/lib/apiUrl";
import { stripDangerousChars } from "@/lib/validation";

interface CustomOption {
  value: string;
  label: string;
  flag: string;
  calling_code: string;
  country_name: string;
  phone_length: number;
}

interface Country {
  id: number;
  country: string;
  short_code: string;
  calling_code: number;
  phone_length: number;
  flag: string;
}

export default function ContactSection() {
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    businessEmail: '',
    message: '',
    honeypot: '',
    country: '',
    countryCode: '',
    "g-recaptcha-response": '',
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [isCountryLoading, setIsCountryLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formFocused, setFormFocused] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHABase>(null);
  const recaptchaSiteKey = "6Lf9iEYqAAAAAGBkHMfKHyrXoDP3tyX_yZ8cL9b-";
  const assetscountries = getFlagUrl();

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      setIsCountryLoading(true);
      try {
        const res = await fetch('/api/countries');
        if (!res.ok) throw new Error('Failed to load countries');
        // console.log(res, "country");
        const data = await res.json();
        // Guard against non-array payloads (error objects, wrapped shapes)
        const list = Array.isArray(data) ? data : Array.isArray(data?.countries) ? data.countries : [];
        setCountries(list);
      } catch (err) {
        console.error("Failed to load countries");
        setCountries([]);
      } finally {
        setIsCountryLoading(false);
      }

    };
    fetchCountries();
  }, []);

  // Auto-detect country from IP or Default to India
  useEffect(() => {
    if (countries.length > 0) {
      // First set default to India
      if (!formData.country) {
        const india = countries.find((c) => c.calling_code === 91);
        if (india) {
          setFormData((prev) => ({
            ...prev,
            country: india.id.toString(),
            countryCode: "+91",
          }));
        }
      }

      // Server-side lookup (no CORS/rate-limit exposure in the browser — see app/api/geo).
      fetch('/api/geo')
        .then((res) => {
          if (!res.ok) throw new Error('API unavailable');
          return res.json();
        })
        .then((data) => {
          const matchingCountry = countries.find((c) => c.short_code === data.country_code);
          if (matchingCountry) {
            setFormData((prev) => ({
              ...prev,
              country: matchingCountry.id.toString(),
              countryCode: `+${matchingCountry.calling_code}`,
            }));
          }
        })
        .catch(() => {
          // Default is already set to India, no action needed on failure
        });
    }
  }, [countries]);


  const customOptions: CustomOption[] = countries.map((country) => ({
    value: country.id.toString(),
    label: `+${country.calling_code}`,
    flag: country.flag ? `${assetscountries}${country.flag}` : "",
    calling_code: `+${country.calling_code}`,
    country_name: country.country,
    phone_length: country.phone_length,
  }));


  const formatOptionLabel = ({ calling_code, flag, country_name }: CustomOption) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {flag && (
        <img
          src={flag}
          alt={`${country_name} flag`}
          style={{ width: '20px', height: '20px', objectFit: 'contain' }}
        />
      )}
      <span>{calling_code}</span>
    </div>
  );


  const countryStyles = {
    control: (provided: any) => ({
      ...provided,
      background: "transparent",
      border: "none",
      boxShadow: "none",
      minWidth: "100px",
      cursor: "pointer",
    }),
    menu: (provided: any) => ({
      ...provided,
      background: "#fff",
      zIndex: 9999,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    }),
    dropdownIndicator: () => ({ display: "none" }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  // Custom styles for react-select
  const countryCodeStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: "8px 0 0 8px",
      background: "white",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      color: "#090D28",
      minWidth: "100px",
      height: "40px",
      border: "2px solid #CEDFFE",
      borderRight: "1px solid #fff",
      outline: "none",
      boxShadow: "none",
      display: "flex",
      alignItems: "center",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: "none",
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: "300px",
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#195AFF",
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        background: "#E6E9FF",
        borderRadius: "3px",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#090D28",
      fontFamily: "Latinka Regular",
      fontSize: "16px",
      display: "flex",
      alignItems: "center",
      height: "100%",
    }),
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let clean: string;
    if (/email/i.test(name)) {
      clean = value;
    } else if (name === 'mobileNumber') {
      const maxLen = countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10;
      clean = value.replace(/\D/g, '').slice(0, maxLen);
    } else {
      clean = stripDangerousChars(value);
    }
    setFormData((prev) => ({ ...prev, [name]: clean }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCountryChange = (option: CustomOption | null) => {
    if (option) {
      setFormData((prev) => ({
        ...prev,
        country: option.value,
        countryCode: option.calling_code,
        mobileNumber: "",
      }));
    }
    setErrors((prev) => ({ ...prev, country: "" }));
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setFormData((prev) => ({ ...prev, "g-recaptcha-response": token || "" }));
    setErrors((prev) => ({ ...prev, captcha: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Name is required";
    if (!formData.mobileNumber.trim()) newErrors.mobileNumber = "Mobile is required";
    else {
      const selectedCountry = countries.find((c) => c.id.toString() === formData.country);
      if (selectedCountry && formData.mobileNumber.length !== selectedCountry.phone_length) {
        newErrors.mobileNumber = `Must be ${selectedCountry.phone_length} digits`;
      }
    }
    if (!formData.businessEmail.trim()) newErrors.businessEmail = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail))
      newErrors.businessEmail = "Invalid email";
    if (!formData.countryCode) newErrors.country = "Select country code";
    if (!recaptchaToken) newErrors.captcha = "Complete reCAPTCHA";
    if (formData.honeypot) newErrors.honeypot = "Spam detected";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the errors");
      return;
    }

    setFormLoading(true);
    try {
      const response = await fetch(getApiUrl("/form-requests"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form_name: "contact form",
          data: {
            name: formData.fullName,
            mobile: formData.mobileNumber,
            comment: formData.message,
            email: formData.businessEmail,
            countryCode: formData.countryCode,
            "g-recaptcha-response": formData["g-recaptcha-response"],
          },
        }),
      });

      const result = await response.json();
      if (response.status === 200) {
        toast.success("Form submitted successfully!");
        setFormData({
          fullName: '',
          mobileNumber: '',
          businessEmail: '',
          message: '',
          honeypot: '',
          country: formData.country,
          countryCode: formData.countryCode,
          "g-recaptcha-response": '',
        });
        setRecaptchaToken(null);
        recaptchaRef.current?.reset();
        setErrors({});
      } else {
        toast.error(result?.error || "Submission failed");
      }
    } catch (err: any) {
      toast.error("Network error. Try again.");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <section className="w-full bg-transparent px-4 md:px-8 xl:px-16">
      <div className="max-w-[1250px] mx-auto flex flex-col xl:flex-row gap-8">
        <div className="xl:w-5/12 w-full">
          <div className="bg-[#2C2B3C] rounded-[12px] p-[24px] w-full h-full xl:w-[480px]">
            <form onSubmit={handleSubmit} className="space-y-[10px]" autoComplete="off" onFocus={() => setFormFocused(true)}>
              {/* Honeypot */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                style={{ display: "none" }}
                tabIndex={-1}
              />

              {/* Full Name */}
              <div>
                <label htmlFor="fullName" className="block text-white text-sm font-medium mb-[6px] schibsted-grotesk">
                  Full name <span className="">*</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter Name"
                  className={`w-full px-[16px] schibsted-grotesk py-[10px] h-[40px] rounded-[8px] bg-white text-gray-900 text-sm focus:outline-none ${errors.fullName ? 'border-2 border-red-500' : ''}`}
                  required
                />
                {errors.fullName && <p className="text-red-400 text-xs mt-1">{errors.fullName}</p>}
              </div>

              {/* Mobile Number */}
              <label htmlFor="mobileNumber" className="block text-white text-sm font-medium mb-[6px] schibsted-grotesk">
                Mobile Number <span className="">*</span>
              </label>
              <div className="mb-4 flex items-center">
                <div className="relative">
                  <Select
                    name="country"
                    instanceId="country-code-select"
                    className='border-none'
                    aria-label="Select country code"
                    options={customOptions}
                    value={customOptions.find((option) => option.value === formData.country) || null}
                    onChange={handleCountryChange}
                    formatOptionLabel={formatOptionLabel}
                    styles={countryCodeStyles}
                    placeholder="+91"

                    isDisabled={isCountryLoading}
                    isLoading={isCountryLoading}
                  />
                  <svg
                    className="absolute right-2 top-1/2 -translate-y-1/2 transform pointer-events-none"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="black"
                      strokeWidth="1.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                {/* <input
                  className={`w-full h-[40px] schibsted-grotesk !px-[16px] !py-[10px] rounded-[8px] border-l-[#fff] rounded-l-none bg-white ${errors.mobile ? 'border-2 border-red-500' : ''
                    }`}
                  type="number"
                  name="mobileNumber"
                  placeholder={
                    isCountryLoading
                      ? "Loading..."
                      : `Enter Mobile`
                  }
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  disabled={isCountryLoading}
                /> */}
                <input
                  id="mobileNumber"
                  className={`w-full h-[40px] schibsted-grotesk !px-[16px] !py-[10px] rounded-[8px] rounded-l-none bg-white 
                    focus:outline-none focus:ring-0 focus:border-transparent
                    ${errors.mobileNumber || errors.country ? 'border-2 border-red-500' : 'border-none border-gray-300'}`}
                  type="tel"
                  name="mobileNumber"
                  aria-label="Mobile number"
                  aria-required="true"
                  placeholder="Enter mobile number"
                  inputMode="numeric"
                  maxLength={countries.find((c) => c.id.toString() === formData.country)?.phone_length || 10}
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  disabled={isCountryLoading}
                />
              </div>
              {(errors.mobileNumber || errors.country) && (
                <p className="mt-1 text-[12px] text-red-500 schibsted-grotesk">{errors.mobileNumber || errors.country}</p>
              )}
              {/* Business Email */}
              <div>
                <label htmlFor="businessEmail" className="block text-white text-sm font-medium mb-[6px] schibsted-grotesk">
                  Business email <span className="">*</span>
                </label>
                <input
                  id="businessEmail"
                  type="email"
                  name="businessEmail"
                  value={formData.businessEmail}
                  onChange={handleChange}
                  placeholder="Enter email"
                  className={`w-full px-4 py-3 h-[40px] schibsted-grotesk rounded-[8px] bg-white text-gray-900 text-sm focus:outline-none ${errors.businessEmail ? 'border-2 border-red-500' : ''}`}
                  required
                />
                {errors.businessEmail && <p className="text-red-400 text-xs mt-1 schibsted-grotesk">{errors.businessEmail}</p>}
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-white text-sm font-medium mb-[6px] schibsted-grotesk">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Write Here.."
                  rows={4}
                  className={`w-full h-[80px] px-4 py-3 schibsted-grotesk rounded-[8px] bg-white text-gray-900 text-sm focus:outline-none resize-none ${errors.message ? 'border-2 border-red-500' : ''
                    }`}
                />
              </div>

              {/* reCAPTCHA */}
              <div className="my-4">
                <div className="relative w-full h-[59px] min-h-[59px] min-[360px]:h-[67px] min-[360px]:min-h-[67px] min-[410px]:h-[78px] min-[410px]:min-h-[78px] overflow-hidden">
                  <div className="absolute top-0 left-0 origin-top-left scale-[0.75] min-[360px]:scale-[0.85] min-[410px]:scale-100">
                    {formFocused ? (
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={recaptchaSiteKey}
                        onChange={handleRecaptchaChange}
                      />
                    ) : (
                      <div
                        className="h-[78px] w-[304px] bg-[#3d3c4e] border border-gray-600 rounded flex items-center justify-center cursor-pointer"
                        onClick={() => setFormFocused(true)}
                      >
                        <span className="text-white schibsted-grotesk text-sm">Click to verify you're human</span>
                      </div>
                    )}
                  </div>
                </div>
                {errors.captcha && <p className="text-red-400 text-xs mt-1 schibsted-grotesk">{errors.captcha}</p>}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={formLoading || !recaptchaToken}
                className={`w-full rounded-[20px] text-white font-semibold schibsted-grotesk py-3 transition-colors duration-200 shadow-lg ${formLoading || !recaptchaToken
                  ? "bg-[#195AFF] cursor-not-allowed"
                  : "bg-[#195AFF]"
                  }`}
              >
                {formLoading ? "Submitting..." : "Submit Message"}
              </button>
            </form>
          </div>
        </div>

        <div className="w-full xl:w-7/12 xl:pl-6 py-[14px] xl:py-0 flex flex-col gap-4">
          {/* Top Row: Unequal columns on medium screens and up (60% / 40%) */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 w-full">
            {/* Card 1: CEO Quote */}
            <div
              className="md:col-span-3 rounded-[16px] p-[24px] shadow-lg flex flex-col justify-between text-white relative overflow-hidden min-h-[220px] border-2 border-[#195AFF]"
              style={{ background: 'linear-gradient(180deg, #4D83FF 0%, #0E307F 100%)' }}
            >
              <p className="text-[14px] md:text-[15px] leading-relaxed schibsted-grotesk opacity-90">
                We are Vilva Business. Proudly Made in Tamil Nadu. Relentlessly working to make Tamil Nadu the heartbeat of global technology. Let’s build the future. Together. From Tamil Nadu. For the world.
              </p>
              <p className="text-[13px] font-medium schibsted-grotesk mt-4 text-right opacity-80">
                - Gopinath Ravirajan, CEO
              </p>
            </div>

            {/* Card 2: Metrics */}
            <div
              className="md:col-span-2 rounded-[16px] p-[24px] shadow-lg flex flex-col justify-between text-white relative overflow-hidden min-h-[220px] border-2 border-[#195AFF]"
              style={{ background: 'linear-gradient(180deg, #4D83FF 0%, #0E307F 100%)' }}
            >
              <div className="flex justify-end">
                <span className="text-[64px] font-bold leading-none font-NeueMontreal">4.9</span>
              </div>
              <div className="mt-4">
                <p className="text-[18px] md:text-[20px] font-bold schibsted-grotesk leading-tight text-right">
                  2.6K+ Positive<br />Feedback
                </p>
                <div className="flex items-center justify-end gap-1 mt-2 text-[14px] font-medium text-white/90">
                  <span className="text-[10px]"><svg xmlns="http://www.w3.org/2000/svg" width="17" height="10" viewBox="0 0 17 10" fill="none">
                    <path d="M15.2171 9.48242C16.3307 9.48242 16.8884 8.13599 16.101 7.34854L9.11875 0.366306C8.63059 -0.121849 7.83914 -0.12185 7.35098 0.366305L0.368749 7.34854C-0.418707 8.13599 0.138999 9.48242 1.25263 9.48242H15.2171Z" fill="white" />
                  </svg></span>
                  <span className='schibsted-grotesk'>+120%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row: Full width bottom card */}
          <div
            className="rounded-[16px] shadow-lg relative overflow-hidden md:overflow-visible w-full flex flex-col md:flex-row min-h-[340px] border-2 border-[#195AFF]"
            style={{ background: 'linear-gradient(180deg, #010839 0%, #091A8F 100%)' }}
          >
            <div className="w-full md:w-6/12 p-[28px] flex flex-col justify-center text-white z-10">
              {/* Vilva Logo/Text */}
              <div className="flex items-center gap-2.5 mb-5">
                {/* WebP, and width/height are the file's real 392x115 — the old 164x59 declared a
                    2.78 aspect for a 3.41 image, so the reserved box was the wrong shape. */}
                <img
                  src={vilvaLogoMark.src}
                  alt="Vilva Logo"
                  fetchPriority="high"
                  width={392}
                  height={115}
                  className="w-[120px] md:w-[163px] h-auto"
                />
              </div>
              <p className="text-[14px] md:text-[15px] leading-relaxed schibsted-grotesk opacity-90">
                Powering GrowthOS for hundreds of businesses across India — and we're just getting started. Every website we build, every payment we process, and every lead we help convert is a step toward putting Tamil Nadu on the global tech map.
              </p>
              <p className="text-[13px] font-medium schibsted-grotesk mt-4 opacity-80">
                - Gopinath Ravirajan, CEO
              </p>
              <Link
                href="/founder"
                prefetch={false}
                className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13px] font-semibold text-[#0E307F] schibsted-grotesk transition-colors hover:bg-blue-50"
              >
                Read More
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
            </div>

            {/* CEO Image on Right */}
            <div className="w-full md:w-6/12 flex items-end justify-center md:absolute md:right-[0px] md:bottom-0 md:h-[680px] z-20">
              {/* Was a 630KB PNG — the wrong container for a photograph, and the largest asset on
                  the homepage (it is also the preloaded priority image). Same 848x1012 pixels as
                  WebP: 39KB. width/height are the true intrinsic size; 400x450 declared a 0.89
                  aspect for a 0.84 image. */}
              <Image
                src={gopiSir}
                alt="Gopinath Ravirajan, CEO"
                width={848}
                height={1012}
                priority
                className="object-contain object-bottom w-auto h-[350px] md:h-[680px]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}